import {
  Injectable,
  inject
} from '@angular/core';

import {
  Router
} from '@angular/router';

// ======================================================
// SERVICES
// ======================================================

// import { ApiService }
// from './api.service';

import { AuthService }
from './auth.service';

import { CookieService }
from './cookie.service';

// import { ProfileService }
// from './profile.service';

import { AlertService }
from './alert.service';

// import { CommunicateService }
// from './communicate.service';

// import { UploaderService }
// from './uploader.service';

// ======================================================
// ENUMS / CONSTANTS
// ======================================================

// import { Route }
// from '../enums/route.enums';

import { RegexLib }
from '../constants/regex.constants';

// ======================================================
// LIBRARIES
// ======================================================

import * as uuid from 'uuid';

import * as cryptoJs
from 'crypto-js';

import dayjs from 'dayjs';

import utc
from 'dayjs/plugin/utc';
import { HttpService } from '../http/http.service';
import { ProfileService } from './profile.service';
import { Route } from '../enums/route.enum';
import { UploaderService } from './uploader.service';

// ======================================================
// TYPES
// ======================================================

type Nullable<T> =
  T | null | undefined;

@Injectable({
  providedIn: 'root'
})
export class SharedService {

  // ======================================================
  // INJECTS
  // ======================================================

  readonly apiService =
    inject(HttpService);

  readonly authService =
    inject(AuthService);

  readonly cookieService =
    inject(CookieService);

  readonly profileService =
    inject(ProfileService);

  readonly alertService =
    inject(AlertService);

//   readonly communicateService =
//     inject(CommunicateService);

  readonly uploaderService =
    inject(UploaderService);

  private readonly router =
    inject(Router);

  // ======================================================
  // PLUGINS
  // ======================================================

  readonly plugins = {
    uuid,
    cryptoJs,
    dayJs: dayjs
  };

  // ======================================================
  // CONSTRUCTOR
  // ======================================================

  constructor() {
    dayjs.extend(utc);
  }

  // ======================================================
  // FILE SIZE FORMATTER
  // ======================================================

  prettyPrintFileSize(
    sizeInBytes: number,
    decimalPlaces = 1
  ): string {

    if (sizeInBytes === 0) {
      return '0.00 B';
    }

    const exponent =
      Math.floor(
        Math.log(sizeInBytes) /
        Math.log(1024)
      );

    return (
      (
        sizeInBytes /
        Math.pow(1024, exponent)
      ).toFixed(decimalPlaces) +
      ' ' +
      ' KMGTP'.charAt(exponent) +
      'B'
    );
  }

  // ======================================================
  // RANDOM COLOR
  // ======================================================

  getRandomColor(): string {

    const letters =
      '0123456789ABCDEF';

    let color = '#';

    for (let i = 0; i < 6; i++) {

      color += letters[
        Math.floor(
          Math.random() * 16
        )
      ];
    }

    return color;
  }

  // ======================================================
  // VALIDATIONS
  // ======================================================

  // OnlyAllowedCharactersPresent(value: string): boolean {

  //   return RegexLib
  //     .OnlyAllowedCharactersRegex
  //     .test(value);
  // }

  OnlyAllowedCharactersPresent(_value: string):boolean {
    return RegexLib.OnlyAllowedCharactersRegex.test(_value);
  }

  OnlyAllowedCharactersPresentInFileName(
    value: string
  ): boolean {

    return RegexLib
      .OnlyAllowedCharactersRegexFileUpload
      .test(value);
  }

  isValidEmail(
    email: string
  ): boolean {

    return RegexLib
      .EmailCharacterValidationRegex
      .test(email);
  }

  // ======================================================
  // VIEWPORT
  // ======================================================

  getViewportHeightInPixels(
    vh: number | string,
    withSymbol = false
  ): number | string {

    const px =
      (
        window.innerHeight *
        parseInt(String(vh), 10)
      ) / 100;

    return withSymbol
      ? `${px}px`
      : px;
  }

  getViewportWidthInPixels(
    vw: number | string,
    withSymbol = false
  ): number | string {

    const px =
      (
        window.innerWidth *
        parseInt(String(vw), 10)
      ) / 100;

    return withSymbol
      ? `${px}px`
      : px;
  }

  // ======================================================
  // SCROLL INTO VIEW
  // ======================================================

  scrollInViewPort(
    id: string,
    options: ScrollIntoViewOptions = {
      behavior: 'smooth',
      block: 'start'
    }
  ): void {

    const element =
      document.getElementById(id);

    if (!element) {

      console.error(
        `Element with ID "${id}" does not exist in DOM`
      );

      return;
    }

    element.scrollIntoView(options);
  }

  // ======================================================
  // UNDEFINED CHECK
  // ======================================================

  isUndefined(
    value: unknown
  ): boolean {

    return (
      ![
        'boolean',
        'number'
      ].includes(typeof value) &&
      !value
    );
  }

  // ======================================================
  // ARRAY EQUALITY
  // ======================================================

  areArraysEqual<T>(
    arr1: T[],
    arr2: T[]
  ): boolean {

    if (
      arr1.length !==
      arr2.length
    ) {
      return false;
    }

    const sorted1 =
      [...arr1].sort();

    const sorted2 =
      [...arr2].sort();

    return sorted1.every(
      (value, index) =>
        value === sorted2[index]
    );
  }

  // ======================================================
  // MEDIA TOKEN
  // ======================================================

  async fetchMediaForStimuli(
    fileURL: string
  ): Promise<string> {

    if (!fileURL) {
      return '';
    }

    if (
      fileURL.includes(
        'drive.google.com/'
      )
    ) {
      return '';
    }

    const fileType =
      fileURL
        .split('.')
        .pop()
        ?.toLowerCase();

    if (!fileType) {
      return fileURL;
    }

    const mediaToken =
      await this.apiService
        .getMediaToken(fileType);

    return `${fileURL}?t=${mediaToken}`;
  }

  // ======================================================
  // PARTICIPANT MEDIA
  // ======================================================

  async fetchMediaForParticipant(
    fileURL: string
  ): Promise<string> {

    if (
      fileURL.includes(
        'drive.google.com/'
      )
    ) {
      return '';
    }

    if (!fileURL) {
      return 'assets/img/person.svg';
    }

    const fileType =
      fileURL
        .split('.')
        .pop()
        ?.toLowerCase();

    if (!fileType) {
      return fileURL;
    }

    const mediaToken =
      await this.apiService
        .getMediaToken(fileType);

    return `${fileURL}?t=${mediaToken}`;
  }

  // ======================================================
  // VIDEO THUMBNAIL
  // ======================================================

  async fetchVideoThumbnail(
    url: string
  ): Promise<string | undefined> {

    try {

      const fileURL =
        url.replace(
          '.mp4',
          '_mp4'
        ) + '.png';

      const fileType =
        fileURL
          .split('.')
          .pop()
          ?.toLowerCase();

      if (!fileType) {
        return fileURL;
      }

      const mediaToken =
        await this.apiService
          .getMediaToken(fileType);

      return `${fileURL}?t=${mediaToken}`;

    } catch (error) {

      console.error(error);

      return undefined;
    }
  }

  // ======================================================
  // NAVIGATION
  // ======================================================

  redirectToPage(
    page: string,
    routeData?: unknown
  ): void {

    switch (page) {

    //   case Route.PROJECT:

    //   case Route.CREATE_PROJECT:

      case Route.PROFILE:

        this.router.navigateByUrl(
          `/${page}`
        );

        break;

      default:

        this.router.navigateByUrl(
          page.startsWith('/')
            ? page
            : `/${page}`
        );

        break;
    }
  }

  // ======================================================
  // STRING FORMAT
  // ======================================================

  convertCamelCaseToDisplayName(
    str: string
  ): string {

    return str.replace(
      /([A-Z])/g,
      ' $1'
    );
  }
}